import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Asset, Delivery, Design, Note, Schedule, Snapshot } from './schemas';

export const DB_NAME = 'memonest';
export const DB_VERSION = 1;

export interface MemoNestDB extends DBSchema {
  notes: { key: string; value: Note; indexes: { updatedAt: number } };
  assets: { key: string; value: Asset; indexes: { hash: string } };
  designs: { key: string; value: Design };
  snapshots: { key: string; value: Snapshot; indexes: { noteId: string; createdAt: number } };
  schedules: { key: string; value: Schedule; indexes: { noteId: string } };
  deliveries: { key: string; value: Delivery; indexes: { scheduleId: string; firedAt: number } };
  meta: { key: string; value: unknown };
}

export type DB = IDBPDatabase<MemoNestDB>;

let dbPromise: Promise<DB> | null = null;

export class StorageUnavailableError extends Error {
  constructor(cause: unknown) {
    super('Local storage (IndexedDB) is unavailable in this browser mode.');
    this.name = 'StorageUnavailableError';
    this.cause = cause;
  }
}

export function getDB(): Promise<DB> {
  if (!dbPromise) {
    if (typeof indexedDB === 'undefined') return Promise.reject(new StorageUnavailableError('indexedDB undefined'));
    dbPromise = openDB<MemoNestDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Each case falls through so a fresh install runs every migration in order.
        switch (oldVersion) {
          case 0: {
            const notes = db.createObjectStore('notes', { keyPath: 'id' });
            notes.createIndex('updatedAt', 'updatedAt');
            const assets = db.createObjectStore('assets', { keyPath: 'id' });
            assets.createIndex('hash', 'hash');
            db.createObjectStore('designs', { keyPath: 'id' });
            const snaps = db.createObjectStore('snapshots', { keyPath: 'id' });
            snaps.createIndex('noteId', 'noteId');
            snaps.createIndex('createdAt', 'createdAt');
            const sched = db.createObjectStore('schedules', { keyPath: 'id' });
            sched.createIndex('noteId', 'noteId');
            const del = db.createObjectStore('deliveries', { keyPath: 'key' });
            del.createIndex('scheduleId', 'scheduleId');
            del.createIndex('firedAt', 'firedAt');
            db.createObjectStore('meta');
          }
          // case 1: future migrations go here.
        }
      },
      blocked() {
        console.warn('MemoNest: database upgrade blocked by another open tab.');
      },
      blocking() {
        // A newer version wants to upgrade: close so it can proceed, then reload to pick up new code.
        dbPromise?.then((db) => db.close());
        dbPromise = null;
      },
      terminated() {
        dbPromise = null;
      },
    }).catch((err) => {
      dbPromise = null;
      throw new StorageUnavailableError(err);
    });
  }
  return dbPromise;
}

/** Test helper: forget the cached connection (used with fake-indexeddb resets). */
export async function closeDB(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise.catch(() => null);
    db?.close();
  }
  dbPromise = null;
}

export function isQuotaError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  return !!e && (e.name === 'QuotaExceededError' || /quota/i.test(e.message ?? ''));
}
